import { useI18n } from "features/translation/context";
import { useCallback, useState } from "react";
import { isGranted } from "pages/productManagement/functions";
import { Permission, Resource } from "pages/productManagement/types";
import { useDiscoverGamesBatchActions } from "./useDiscoverGamesBatchActions";
import { useDiscoverGamesColumnConfig } from "./useDiscoverGamesColumnConfig";
import { useDiscoverGamesFilterHandlers } from "./useDiscoverGamesFilterHandlers";
import { useDiscoverGamesFilters } from "./useDiscoverGamesFilters";
import { useDiscoverGamesProgramGameModal } from "./useDiscoverGamesProgramGameModal";
import { useDiscoverGamesQuery } from "./useDiscoverGamesQuery";
import { useDiscoverGamesTableColumns } from "./useDiscoverGamesTableColumns";
import { useDiscoverGamesViewState } from "./useDiscoverGamesViewState";

interface UseDiscoverGamesTableControllerOptions {
	storageKey: string;
	initialColumnVisibility: Record<string, boolean>;
}

export function useDiscoverGamesTableController({
	storageKey,
	initialColumnVisibility,
}: UseDiscoverGamesTableControllerOptions) {
	const { locale } = useI18n();
	const [gameAnalysisGameId, setGameAnalysisGameId] = useState<number | null>(null);
	const openGameAnalysisModal = useCallback((gameId: number) => {
		setGameAnalysisGameId(gameId);
	}, []);
	const closeGameAnalysisModal = useCallback(() => {
		setGameAnalysisGameId(null);
	}, []);
	const colConfig = useDiscoverGamesColumnConfig(locale, openGameAnalysisModal);
	const view = useDiscoverGamesViewState({
		storageKey,
		discoverGamesColumns: colConfig.discoverGamesColumns,
		initialColumnVisibility,
	});
	const filters = useDiscoverGamesFilters({ storageKey });
	const query = useDiscoverGamesQuery({
		storageKey,
		apiFilters: filters.apiFilters,
		apiColumns: view.apiColumns,
	});
	const batch = useDiscoverGamesBatchActions();
	const programGameModal = useDiscoverGamesProgramGameModal();
	const { handleFilterChange, handleClearFilters } = useDiscoverGamesFilterHandlers(filters, query, batch);
	const simpleColumns = useDiscoverGamesTableColumns(colConfig, view, query);
	const canProgramGame = isGranted(Resource.GAME_UPLOADS, Permission.GAME_CREATE_GAME);
	// Bulk update edits managers/assignee/priority/game status; allow it only when the user can
	// edit at least one of those fields (mirrors the per-field gating inside the bulk modal).
	const canBulkUpdate =
		colConfig.canEditGameUploads ||
		colConfig.canEditAssignee ||
		colConfig.canEditGameStatus ||
		colConfig.canEditPriority;

	return {
		colConfig,
		view,
		filters,
		query,
		batch,
		programGameModal,
		gameAnalysisModal: {
			gameId: gameAnalysisGameId,
			close: closeGameAnalysisModal,
		},
		handleFilterChange,
		handleClearFilters,
		simpleColumns,
		canProgramGame,
		canBulkUpdate,
	};
}
